import { Button, Card, Grid } from '@mantine/core';
import type { MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import SubmitActionSplitButton, {
  type SubmitActionMenuItem,
} from '../SubmitActionSplitButton/SubmitActionSplitButton';
import classes from './CatalogEditCreateFooter.module.css';

export default function CatalogEditCreateFooter({
  onCancel,
  loading,
  disableCancel = false,
  disableSubmit = false,
  hideSubmit = false,
  hideCancel = false,
  showPrint = false,
  onPrint = () => {},
  submitBtnText = '',
  printBtnText = '',
  submitButtonTestId,
  printButtonTestId,
  disablePrint = false,
  secondarySubmitActions,
  submitMenuAriaLabel,
  submitMenuButtonTestId,
}: {
  onCancel?: () => void;
  loading: boolean;
  submitBtnText?: string;
  printBtnText?: string;
  disableCancel?: boolean;
  disableSubmit?: boolean;
  disablePrint?: boolean;
  hideSubmit?: boolean;
  hideCancel?: boolean;
  showPrint?: boolean;
  onPrint?: MouseEventHandler<HTMLButtonElement>;
  submitButtonTestId?: string;
  printButtonTestId?: string;
  /** Variants of the primary submit action, such as "save and print". */
  secondarySubmitActions?: SubmitActionMenuItem[];
  submitMenuAriaLabel?: string;
  submitMenuButtonTestId?: string;
}) {
  const { t } = useTranslation(['CommonButtons']);
  const history = useHistory();
  const submitLabel = submitBtnText ? submitBtnText : t('CommonButtons:submit');
  const hasSecondarySubmitActions = Boolean(secondarySubmitActions?.length);

  return (
    <Card pos={'sticky'} bottom={0}>
      <Card.Section inheritPadding py="xs">
        <Grid align="center">
          {!hideCancel && (
            <Grid.Col
              span={{ xs: 12, md: 6 }}
              className={hasSecondarySubmitActions ? classes.secondarySubmitCancel : undefined}>
              <Button
                fullWidth
                color="gray"
                loading={loading}
                disabled={disableCancel}
                onClick={(e) => {
                  e.preventDefault();
                  history.goBack();
                  if (onCancel) onCancel();
                }}>
                {t('CommonButtons:cancel')}
              </Button>
            </Grid.Col>
          )}
          {!hideSubmit && (
            <Grid.Col
              span={{ xs: 12, md: 6 }}
              className={hasSecondarySubmitActions ? classes.secondarySubmit : undefined}>
              {hasSecondarySubmitActions ? (
                <SubmitActionSplitButton
                  submitLabel={submitLabel}
                  submitDisabled={disableSubmit}
                  loading={loading}
                  menuAriaLabel={submitMenuAriaLabel || t('CommonButtons:saveOptions')}
                  actions={secondarySubmitActions || []}
                  submitButtonTestId={submitButtonTestId}
                  menuButtonTestId={submitMenuButtonTestId}
                />
              ) : (
                <Button
                  data-testid={submitButtonTestId}
                  fullWidth
                  type="submit"
                  loading={loading}
                  disabled={disableSubmit}>
                  {submitLabel}
                </Button>
              )}
            </Grid.Col>
          )}
          {showPrint && (
            <Grid.Col span={{ xs: 12, md: 6 }}>
              <Button
                data-testid={printButtonTestId}
                fullWidth
                type="button"
                loading={loading}
                disabled={disablePrint}
                onClick={onPrint}>
                {printBtnText ? printBtnText : t('CommonButtons:print')}
              </Button>
            </Grid.Col>
          )}
        </Grid>
      </Card.Section>
    </Card>
  );
}
